import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HostAppShell } from '../components/HostAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, Calendar, Car, Clock, DollarSign, AlertTriangle,
  TrendingUp, ArrowUpRight, ArrowDownRight, CheckCircle,
  Loader2, Lightbulb, Target, Camera, MessageSquare,
  ShieldAlert, MapPin, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_STYLE = {
  urgent: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700 border-red-200' },
  important: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  optimisation: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-600', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
};

const ACTION_ICONS = {
  pending_request: Calendar,
  incomplete_listing: Camera,
  no_activity: Car,
  expired_approvals: Clock,
};

const INSIGHT_STYLE = {
  warning: { bg: 'bg-amber-50 border-amber-200', icon: 'bg-amber-100 text-amber-600' },
  opportunity: { bg: 'bg-blue-50 border-blue-200', icon: 'bg-blue-100 text-blue-600' },
  info: { bg: 'bg-green-50 border-green-200', icon: 'bg-green-100 text-green-600' },
};

const formatCurrency = (amount) => new Intl.NumberFormat('en-AU', {
  style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0
}).format(amount || 0);

const HostAiAssistantPage = () => {
  const { api, hasFeature, getSubscriptionTier } = useAuth();
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);
  const tier = getSubscriptionTier();
  const hasAssistant = hasFeature('ai_assistant');

  useEffect(() => {
    if (!hasAssistant) { setLoading(false); return; }
    const fetchBriefing = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/host/assistant/briefing?days=${period}`);
        setBriefing(res.data);
      } catch {
        toast.error('Could not load briefing');
      }
      setLoading(false);
    };
    fetchBriefing();
  }, [period, hasAssistant]);

  // Locked state for non-entitled hosts
  if (!hasAssistant) {
    return (
      <HostAppShell title="AI Assistant" activeId="ai_assistant">
        <div className="max-w-2xl mx-auto py-8 space-y-6">
          <Card className="border-0 bg-gradient-to-br from-violet-50 to-indigo-50 shadow-sm" data-testid="assistant-locked-card">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto">
                <Sparkles className="w-7 h-7 text-violet-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Your AI Assistant</h2>
              <p className="text-sm text-slate-600 max-w-md mx-auto">
                Get daily briefings, actionable insights, and smart recommendations to grow your fleet business.
              </p>
              <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => navigate('/host/plan')} data-testid="assistant-upgrade-btn">
                Upgrade to Pro
              </Button>
            </CardContent>
          </Card>
          {/* Blurred preview */}
          <div className="relative">
            <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 rounded-xl flex items-center justify-center">
              <div className="flex items-center gap-2 text-slate-400">
                <Lock className="w-4 h-4" />
                <span className="text-sm font-medium">Pro feature</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 opacity-50" data-testid="assistant-preview-cards">
              {['Bookings Today', 'Active Trips', 'Pending Requests', 'Weekly Earnings'].map(label => (
                <Card key={label} className="border-slate-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-400">{label}</p>
                    <p className="text-2xl font-bold text-slate-300 mt-1">--</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </HostAppShell>
    );
  }

  if (loading) {
    return (
      <HostAppShell title="AI Assistant" activeId="ai_assistant">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      </HostAppShell>
    );
  }

  const summary = briefing?.summary || {};
  const actions = briefing?.actions || [];
  const performance = briefing?.performance || {};
  const suggestions = briefing?.suggestions || [];
  const insights = briefing?.insights || [];
  const risks = briefing?.risks || [];
  const isPremium = tier === 'premium';

  return (
    <HostAppShell title="AI Assistant" activeId="ai_assistant">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="assistant-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Daily Briefing</h1>
              <p className="text-xs text-slate-500">Your fleet at a glance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map(d => (
              <Button key={d} variant={period === d ? 'default' : 'outline'} size="sm"
                className={`h-8 text-xs ${period === d ? 'bg-violet-600 hover:bg-violet-700' : ''}`}
                onClick={() => setPeriod(d)} data-testid={`period-${d}`}>
                {d}d
              </Button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="assistant-summary">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <Calendar className="w-3.5 h-3.5 text-blue-500" />
                <p className="text-xs text-slate-500">Today</p>
              </div>
              <p className="text-2xl font-bold text-slate-900" data-testid="bookings-today">{summary.bookings_today || 0}</p>
              <p className="text-[11px] text-slate-400">bookings</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <Car className="w-3.5 h-3.5 text-indigo-500" />
                <p className="text-xs text-slate-500">Active</p>
              </div>
              <p className="text-2xl font-bold text-slate-900" data-testid="active-trips">{summary.active_trips || 0}</p>
              <p className="text-[11px] text-slate-400">trips</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <Clock className="w-3.5 h-3.5 text-amber-500" />
                <p className="text-xs text-slate-500">Pending</p>
              </div>
              <p className="text-2xl font-bold text-slate-900" data-testid="pending-requests">{summary.pending_requests || 0}</p>
              <p className="text-[11px] text-slate-400">requests</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 border-0">
            <CardContent className="p-4 text-white">
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign className="w-3.5 h-3.5" />
                <p className="text-xs opacity-80">Earnings</p>
              </div>
              <p className="text-2xl font-bold" data-testid="period-earnings">{formatCurrency(summary.earnings_period)}</p>
              <p className="text-[11px] opacity-70">last {period} days</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions Required */}
        {actions.length > 0 && (
          <Card className="border-slate-200" data-testid="assistant-actions">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-4 h-4 text-red-500" />
                Action Required
                <Badge className="bg-red-100 text-red-700 border-red-200 border text-[10px]">{actions.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {actions.map((action, idx) => {
                const style = PRIORITY_STYLE[action.priority] || PRIORITY_STYLE.optimisation;
                const Icon = ACTION_ICONS[action.type] || AlertTriangle;
                return (
                  <div key={idx} className={`flex items-start gap-3 p-3 rounded-xl border ${style.bg}`} data-testid={`action-${idx}`}>
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${style.text}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${style.text}`}>{action.message}</p>
                    </div>
                    <Badge className={`text-[10px] px-1.5 py-0 h-4 border flex-shrink-0 ${style.badge}`}>
                      {action.priority}
                    </Badge>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Performance Snapshot */}
        <Card className="border-slate-200" data-testid="assistant-performance">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#004B8D]" />
              Performance Snapshot
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500">Revenue Change</p>
                <div className={`flex items-center gap-1 mt-1 text-lg font-bold ${
                  performance.revenue_change_pct > 0 ? 'text-green-600' :
                  performance.revenue_change_pct < 0 ? 'text-red-600' : 'text-slate-600'
                }`} data-testid="revenue-change">
                  {performance.revenue_change_pct > 0 ? <ArrowUpRight className="w-4 h-4" /> :
                   performance.revenue_change_pct < 0 ? <ArrowDownRight className="w-4 h-4" /> : null}
                  {Math.abs(performance.revenue_change_pct || 0)}%
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500">Fleet Utilisation</p>
                <p className="text-lg font-bold text-slate-900 mt-1" data-testid="utilisation">{performance.utilisation_pct || 0}%</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">This Period</p>
                <p className="text-lg font-bold text-green-600 mt-1">{formatCurrency(performance.earnings_this_period)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Previous Period</p>
                <p className="text-lg font-bold text-slate-400 mt-1">{formatCurrency(performance.earnings_prev_period)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Suggestions (Pro+) */}
        {suggestions.length > 0 && (
          <Card className="border-slate-200" data-testid="assistant-suggestions">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                Suggestions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {suggestions.map((s, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100" data-testid={`suggestion-${idx}`}>
                  <MessageSquare className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
                  <p className="text-sm text-slate-600">{s.message}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Premium: Smart Insights */}
        {isPremium && insights.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/10" data-testid="assistant-insights">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-600" />
                Smart Insights
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] border">Premium</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {insights.map((insight, idx) => {
                const style = INSIGHT_STYLE[insight.severity] || INSIGHT_STYLE.info;
                return (
                  <div key={idx} className={`flex items-start gap-3 p-3 rounded-xl border ${style.bg}`} data-testid={`insight-${idx}`}>
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${style.icon}`}>
                      <Lightbulb className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{insight.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{insight.message}</p>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Premium: Risks */}
        {isPremium && risks.length > 0 && (
          <Card className="border-red-200 bg-red-50/10" data-testid="assistant-risks">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-red-500" />
                Risk Alerts
                <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] border">{risks.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {risks.map((risk, idx) => (
                <div key={idx} className={`flex items-start gap-3 p-3 rounded-xl border ${
                  risk.severity === 'high' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                }`} data-testid={`risk-${idx}`}>
                  {risk.type === 'gps_alert' ? <MapPin className="w-4 h-4 mt-0.5 text-red-500 flex-shrink-0" /> :
                   <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" />}
                  <div>
                    <p className="text-sm font-medium text-slate-800">{risk.message}</p>
                    <Badge className={`mt-1 text-[10px] ${risk.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {risk.severity}
                    </Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Premium teaser for Pro */}
        {!isPremium && (
          <Card className="border-dashed border-amber-200 bg-amber-50/20" data-testid="premium-insights-teaser">
            <CardContent className="p-6 text-center">
              <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-800">Smart Insights & Risk Alerts</h3>
              <p className="text-sm text-slate-500 mt-1">Get data-driven recommendations, risk alerts, and opportunity detection with Premium.</p>
              <Button className="mt-4 bg-amber-600 hover:bg-amber-700 text-sm h-9" onClick={() => navigate('/host/plan')} data-testid="upgrade-premium-btn">
                Upgrade to Premium
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </HostAppShell>
  );
};

export default HostAiAssistantPage;
